<template>
  <page-template :is-loading="isLoading" :data-exists="selectedBackendServiceData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('backendService.configPage.newPageHeadline')
          : $t('backendService.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft>
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Backend Services List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedBackendServiceData && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          v-if="selectedBackendService"
          :items="dropdownData.map((item): [string, string] => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedBackendService"
          @update:value="switchBackendService($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('backendService.configPage.switchTooltip')"
          classes="switch-backend-service switch-page-entry"
          data-qa="switch-backend-service"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight>
      <template v-if="selectedBackendServiceData && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateBackendService()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadBackendService()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteBackendServiceModal()"
          class="delete-button"
          :disabled="
            isLoading || selectedBackendServiceProtected || selectedBackendServiceReferenced
          "
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isBackendServiceValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('backendService.configPage.name')"
              :placeholder="$t('backendService.configPage.name')"
              v-model:inputValue="selectedBackendServiceData.name"
            />
          </div>
          <div class="rbz-field rbz-field-toggle http11-field">
            <rbz-form-toggle
              v-model:buttonValue="selectedBackendServiceData.http11"
              :label="$t('backendService.configPage.http11')"
              data-qa="http11-toggle"
            />
            <span v-b-tooltip="$t('backendService.configPage.http11AdditionalInfoTooltip')">
              <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
            </span>
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('backendService.configPage.description')"
              v-model:inputValue="selectedBackendServiceData.description"
              rows="2"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              :items="protocols"
              class="transport-mode-selection"
              data-qa="transport-mode-selection"
              v-model:value="selectedBackendServiceData.transport_mode"
            >
              <template #labelOverride>
                <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
                  {{ $t('backendService.configPage.transportProtocol') }}
                  <span
                    v-b-tooltip="
                      $t('backendService.configPage.transportProtocolAdditionalInfoTooltip')
                    "
                  >
                    <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
                  </span>
                </label>
              </template>
            </rbz-dropdown>
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              :items="
                BACKEND_SERVICES_STICKINESS_MODELS.map((stickinessModel): [string, string] => {
                  return [stickinessModel.value, stickinessModel.name];
                }) as [string, string][]
              "
              :label="$t('backendService.configPage.loadBalancingStickinessModel')"
              class="load-balancing-stickiness-selection"
              data-qa="load-balancing-stickiness-selection"
              v-model:value="selectedBackendServiceData.sticky"
              @update:value="onUpdateStickyValue"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              v-if="selectedBackendServiceData.sticky === 'customcookie'"
              name="sticky-cookie-name"
              classes="sticky-cookie-name"
              data-qa="sticky-cookie-name-input"
              :label="$t('backendService.configPage.stickyCookieName')"
              :placeholder="$t('backendService.configPage.stickyCookieName')"
              v-model:inputValue="selectedBackendServiceData.sticky_cookie_name"
            />
          </div>
          <div class="rbz-field" v-if="isServerToBackendMtlsCertificateEnabled">
            <rbz-dropdown
              :items="mapServerToBackendMtlsCertificates"
              v-model:value="selectedBackendServiceData['mtls_certificate']"
              :label="$t('backendService.configPage.serverToBackendMtlsCertificate')"
              class="server-to-backend-mtls-certificate-dropdown"
              data-qa="server-to-backend-mtls-certificate-dropdown"
            >
              <template #postLabel>
                <div class="rbz-field-info">
                  <b-tooltip target="tooltip-server-to-backend-mtls-certificate">
                    {{ $t('backendService.configPage.serverToBackendMtlsCertificateTooltip') }}
                  </b-tooltip>
                  <icon-info-small
                    color="dusty-gray"
                    id="tooltip-server-to-backend-mtls-certificate"
                    class="info-icon"
                  />
                </div>
              </template>
            </rbz-dropdown>
          </div>
          <div class="rbz-field" v-if="isServerToBackendCaCertificateEnabled">
            <rbz-dropdown
              :items="mapServerToBackendCaCertificates"
              v-model:value="selectedBackendServiceData['mtls_trusted_certificate']"
              :label="$t('backendService.configPage.serverToBackendCaCertificate')"
              class="server-to-backend-ca-certificate-dropdown"
              data-qa="server-to-backend-ca-certificate-dropdown"
            >
            </rbz-dropdown>
          </div>
        </div>
      </div>
      <rbz-table
        :columns="hostsColumns"
        :table-data="selectedBackendServiceData['back_hosts']"
        :row-buttons="hostsRowButtons"
        :show-new-as-row="true"
        :show-new-default-data="defaultNewHost"
        :show-filter-button="false"
        :show-download-button="false"
        rows-per-page="20"
        ref="hostsTable"
        class="hosts-table"
        data-qa="hosts-table"
        @add-inline-data="
          ((host: BackendServiceHost) => addHost(host)) as (value: GenericObject) => void
        "
        @edit-inline-data="
          ((host: BackendServiceHost) => editHost(host)) as (value: GenericObject) => void
        "
        @delete-row-button-clicked="
          ((host: BackendServiceHost) => deleteHost(host)) as (value: GenericObject) => void
        "
      >
        <template #httpPortOverrideNewInput="rowProps">
          <rbz-dropdown
            type="multi-select"
            :items="
              (((rowProps as GenericObject).data['http_ports'] as number[])
                ?.sort((a, b) => a - b)
                ?.map((port): [number, string] => [port, port.toString()]) || []) as [
                number,
                string,
              ][]
            "
            :value="(rowProps as GenericObject).data['http_ports']"
            @update:value="
              ((rowProps as GenericObject).data['http_ports'] as number[]) = (
                $event as string[]
              ).map(Number)
            "
            @search="
              addHostPort(
                (rowProps as GenericObject).data as BackendServiceHost,
                'http_ports',
                $event
              )
            "
            :filter-force-display="true"
            filter-variant="search-trigger"
            :tag-click-enabled="false"
            :all-visible="false"
            :placeholder="$t('backendService.configPage.backHosts.httpPort')"
            classes="http-port-selection"
            data-qa="http-port-selection"
          />
        </template>
        <template #httpsPortOverrideNewInput="rowProps">
          <rbz-dropdown
            type="multi-select"
            :items="
              (((rowProps as GenericObject).data['https_ports'] as number[])
                ?.sort((a, b) => a - b)
                ?.map((port): [number, string] => [port, port.toString()]) || []) as [
                number,
                string,
              ][]
            "
            :value="(rowProps as GenericObject).data['https_ports']"
            @update:value="
              ((rowProps as GenericObject).data['https_ports'] as number[]) = (
                $event as string[]
              ).map(Number)
            "
            @search="
              addHostPort(
                (rowProps as GenericObject).data as BackendServiceHost,
                'https_ports',
                $event
              )
            "
            :filter-force-display="true"
            filter-variant="search-trigger"
            :tag-click-enabled="false"
            :all-visible="false"
            :placeholder="$t('backendService.configPage.backHosts.httpsPort')"
            classes="https-port-selection"
            data-qa="https-port-selection"
          />
        </template>
        <template #weightOverrideNewInput="rowProps">
          <rbz-form-input
            name="weight"
            data-qa="weight-input"
            classes="weight-input"
            :placeholder="$t('backendService.configPage.backHosts.weight')"
            type="number"
            number-type="whole"
            :is-invalid="!isValidWeight((rowProps as GenericObject).data['weight'] as number)"
            :tooltip="
              !isValidWeight((rowProps as GenericObject).data['weight'] as number)
                ? $t('backendService.configPage.backHosts.invalidWeight')
                : ''
            "
            :disabled="isSingleHost && (rowProps as GenericObject).mode === 'edit'"
            v-model:inputValue="(rowProps as GenericObject).data['weight']"
          />
        </template>
        <template #maxFailsOverrideNewInput="rowProps">
          <rbz-form-input
            name="max-fails"
            data-qa="max-fails-input"
            classes="max-fails-input"
            :placeholder="$t('backendService.configPage.backHosts.maxFails')"
            type="number"
            number-type="whole"
            :is-invalid="!isValidMaxFails((rowProps as GenericObject).data['max_fails'] as number)"
            :tooltip="
              !isValidMaxFails((rowProps as GenericObject).data['max_fails'] as number)
                ? $t('backendService.configPage.backHosts.invalidMaxFails')
                : ''
            "
            :disabled="isSingleHost && (rowProps as GenericObject).mode === 'edit'"
            v-model:inputValue="(rowProps as GenericObject).data['max_fails']"
          />
        </template>
        <template #failTimeoutOverrideNewInput="rowProps">
          <rbz-form-input
            name="failTimeout"
            data-qa="fail-timeout-input"
            classes="fail-timeout-input"
            :placeholder="$t('backendService.configPage.backHosts.failTimeout')"
            type="number"
            number-type="whole"
            :is-invalid="
              !isValidFailTimeout((rowProps as GenericObject).data['fail_timeout'] as number)
            "
            :tooltip="
              !isValidFailTimeout((rowProps as GenericObject).data['fail_timeout'] as number)
                ? $t('backendService.configPage.backHosts.invalidFailTimeout')
                : ''
            "
            v-model:inputValue="(rowProps as GenericObject).data['fail_timeout']"
          />
        </template>
        <template #isDownOverrideNewInput="rowProps">
          <form-checkbox
            :checked="(rowProps as GenericObject).data.down"
            classes="downable-checkbox"
            data-qa="downable-checkbox"
            @update:checkedValue="(rowProps as GenericObject).data.down = $event"
            :disabled="!isHostDownable((rowProps as GenericObject).data as BackendServiceHost)"
            :id="`host-down-${(rowProps as GenericObject).data.id}`"
          />
        </template>
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadBackendService(selectedBackendService);
          loadDropdownData();
        "
      />
      <rbz-modal-dialog
        :show="showDeleteBackendServiceModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteBackendService"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteBackendServiceModal = false"
        @closeModal="showDeleteBackendServiceModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedBackendServiceData.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import _, { isInteger } from 'lodash';
import RequestsUtils from '@/helpers/RequestsUtils';
import {
  BackendService,
  BackendServiceHost,
  BasicDocument,
  Certificate,
  DocumentName,
} from '@/types';
import Utils from 'ui-component-library/helpers/Utils';
import { defineComponent } from 'vue';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import FormCheckbox from '@/components/RbzFormCheckbox.vue';
import { BACKEND_SERVICES_STICKINESS_MODELS } from '@/types/const';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import {
  RbzTableColumnOptions,
  RbzTableRowButton,
  GenericObject,
} from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'BackendServicesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    GitHistory,
    IconInfoSmall,
    RbzFormButton,
    FormCheckbox,
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      selectedBackendService: null as string,
      selectedBackendServiceData: null as BackendService,
      originalBackendServiceData: null as BackendService,
      pageLoadingCounter: 0,
      isDeleteLoading: false,
      showDeleteBackendServiceModal: false,
      isNew: false,

      certificates: [] as Certificate[],
      referencedIds: [] as string[],
      BACKEND_SERVICES_STICKINESS_MODELS,
      isServerToBackendMtlsCertificateEnabled: false,
      isServerToBackendCaCertificateEnabled: false,

      // Hosts table
      defaultNewHost: {
        http_ports: [80],
        https_ports: [443],
        weight: 1,
        fail_timeout: 10,
        down: false,
        host: '',
        max_fails: 0,
        monitor_state: '',
        backup: false,
      },
      hostsColumns: [
        {
          id: 'host',
          title: this.$t('backendService.configPage.backHosts.host'),
          fieldNames: ['host'],
          validate: (host: BackendServiceHost) => {
            const invalidHosts = /^(localhost|127(?:.\d{1,3}){3}|0\.0\.0\.0|::1|::0|::|$)/;
            if (invalidHosts.test(host['host'].trim())) {
              return false;
            }
            return true;
          },
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'http-ports',
          title: this.$t('backendService.configPage.backHosts.httpPort'),
          fieldNames: ['http_ports'],
          overrideNewInput: 'httpPortOverrideNewInput',
          displayFunction: (host: BackendServiceHost) => {
            return (
              host['http_ports']
                ?.sort((a, b) => {
                  return a - b;
                })
                ?.join(', ') || ''
            );
          },
          classes: 'width-250px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'https-ports',
          title: this.$t('backendService.configPage.backHosts.httpsPort'),
          fieldNames: ['https_ports'],
          overrideNewInput: 'httpsPortOverrideNewInput',
          displayFunction: (host: BackendServiceHost) => {
            return (
              host['https_ports']
                ?.sort((a, b) => {
                  return a - b;
                })
                ?.join(', ') || ''
            );
          },
          classes: 'width-250px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'weight',
          title: this.$t('backendService.configPage.backHosts.weight'),
          fieldNames: ['weight'],
          overrideNewInput: 'weightOverrideNewInput',
          validate: (host: BackendServiceHost) => {
            return this.isValidWeight(host.weight);
          },
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'max-fails',
          title: this.$t('backendService.configPage.backHosts.maxFails'),
          fieldNames: ['max_fails'],
          overrideNewInput: 'maxFailsOverrideNewInput',
          validate: (host: BackendServiceHost) => {
            return this.isValidMaxFails(host.max_fails);
          },
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'fail-timeout',
          title: this.$t('backendService.configPage.backHosts.failTimeout'),
          fieldNames: ['fail_timeout'],
          overrideNewInput: 'failTimeoutOverrideNewInput',
          validate: (host: BackendServiceHost) => {
            return this.isValidFailTimeout(host.fail_timeout);
          },
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'down',
          title: this.$t('backendService.configPage.backHosts.isDown'),
          fieldNames: ['down'],
          displayFunction: (item: BackendServiceHost) => {
            return item.down ? this.$t('common.yes') : this.$t('common.no');
          },
          overrideNewInput: 'isDownOverrideNewInput',
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      hostsRowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          editInline: true,
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
          disabledCallback: () => {
            return this.isSingleHost;
          },
          tooltipCallback: () => {
            if (this.isSingleHost) {
              return this.$t('backendService.configPage.backHosts.deleteDisabledTooltip');
            }
            return this.$t('common.operations.delete.tableTooltip');
          },
        },
      ] as RbzTableRowButton[],
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, usePlanetStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('backendService.plural'),
        singular: this.$t('backendService.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/backend-services/versions`;
    },

    isLoading(): boolean {
      return this.pageLoadingCounter > 0 || this.isDeleteLoading;
    },

    selectedBackendServiceProtected(): boolean {
      return this.selectedBackendService?.startsWith('__');
    },

    selectedBackendServiceReferenced(): boolean {
      return this.referencedIds.includes(this.selectedBackendService);
    },

    deleteTooltip(): string {
      if (this.selectedBackendServiceProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedBackendServiceReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedBackendServiceData, this.originalBackendServiceData);
    },

    isSingleHost(): boolean {
      return this.selectedBackendServiceData['back_hosts']?.length === 1;
    },

    protocols(): [string, string][] {
      const map = {
        default: this.$t('backendService.transportProtocolValue.perRequest'),
        http: this.$t('backendService.transportProtocolValue.http'),
        https: this.$t('backendService.transportProtocolValue.https'),
        port_bridge: this.$t('backendService.transportProtocolValue.portBridge'),
      };
      return _.map(map, (name, value) => {
        return [value, name];
      });
    },

    areHostsValid(): boolean {
      return _.every(
        this.selectedBackendServiceData['back_hosts'],
        (host) =>
          this.isValidWeight(host.weight) &&
          this.isValidMaxFails(host.max_fails) &&
          this.isValidFailTimeout(host.fail_timeout)
      );
    },
    isBackendServiceValid(): boolean {
      const trimmedName = this.selectedBackendServiceData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedBackendServiceData.name &&
        (trimmedName === this.originalBackendServiceData.name.toLowerCase().trim() || !nameExists);
      return nameValid && this.areHostsValid;
    },

    mapServerToBackendMtlsCertificates(): [string, string][] {
      const certificates = _.filter(this.certificates, (certificate: Certificate) => {
        return certificate.side === 'serverToBackendMTLS';
      }).map((item): [string, string] => [item.id, item.name]);
      certificates.unshift([
        'off',
        this.$t('backendService.configPage.serverToBackendMtlsCertificateOff'),
      ]);
      return certificates;
    },

    mapServerToBackendCaCertificates(): [string, string][] {
      const certificates = _.filter(this.certificates, (certificate: Certificate) => {
        return certificate.side === 'backendCA';
      }).map((item): [string, string] => [item.id, item.name]);
      certificates.unshift([
        'off',
        this.$t('backendService.configPage.serverToBackendCaCertificateOff'),
      ]);
      return certificates;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    isValidWeight(weight: number): boolean {
      return isInteger(weight) && weight > 0;
    },

    isValidMaxFails(maxFails: number): boolean {
      return isInteger(maxFails) && maxFails >= 0;
    },

    isValidFailTimeout(failTimeout: number): boolean {
      return isInteger(failTimeout) && failTimeout >= 0;
    },

    redirectToList() {
      this.navigateTo(this.getListPath('backend-services'));
    },

    switchBackendService(backendServiceId: string) {
      this.navigateTo(this.getEditPath('backend-services', backendServiceId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/backend-services`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    async loadBackendService(backendServiceId: string) {
      this.setPageLoadingStatus(true);
      this.selectedBackendService = backendServiceId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/backend-services/${backendServiceId}`,
      });
      this.selectedBackendServiceData = response?.data || {};
      _.forEach(this.selectedBackendServiceData['back_hosts'], (host) => {
        host.id = DatasetsUtils.generateUUID2();
      });
      if (!this.selectedBackendServiceData['mtls_certificate']) {
        this.selectedBackendServiceData['mtls_certificate'] = 'off';
      }
      if (!this.selectedBackendServiceData['mtls_trusted_certificate']) {
        this.selectedBackendServiceData['mtls_trusted_certificate'] = 'off';
      }
      this.originalBackendServiceData = _.cloneDeep(this.selectedBackendServiceData);
      this.setPageLoadingStatus(false);
    },

    stripFrontendOnlyInfo(backendService: BackendService) {
      _.forEach(backendService['back_hosts'], (entry) => {
        delete entry.id;
        entry.host = entry.host.trim();
      });
      if (backendService['mtls_certificate'] === 'off') {
        backendService['mtls_certificate'] = '';
      }
      if (backendService['mtls_trusted_certificate'] === 'off') {
        backendService['mtls_trusted_certificate'] = '';
      }
    },

    async duplicateBackendService() {
      this.setPageLoadingStatus(true);
      const backendServiceToAdd = _.cloneDeep(this.selectedBackendServiceData);
      backendServiceToAdd.id = DatasetsUtils.generateUUID2();
      backendServiceToAdd.name = Utils.generateUniqueEntityName(
        backendServiceToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      this.stripFrontendOnlyInfo(backendServiceToAdd);
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/backend-services/${backendServiceToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: backendServiceToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchBackendService(backendServiceToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadBackendService() {
      Utils.downloadFile('backend-service', 'json', this.selectedBackendServiceData);
    },

    openDeleteBackendServiceModal() {
      this.showDeleteBackendServiceModal = true;
    },

    async deleteBackendService() {
      this.isDeleteLoading = true;
      const backendServiceId = this.selectedBackendService;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === backendServiceId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedBackendService = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/backend-services/${backendServiceId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedBackendServiceData.name = this.selectedBackendServiceData.name.trim();
      const backendServiceToSave = _.cloneDeep(this.selectedBackendServiceData);
      this.stripFrontendOnlyInfo(backendServiceToSave);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/backend-services/${this.selectedBackendServiceData.id}`,
        data: backendServiceToSave,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedBackendServiceData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
        }
      }
      this.originalBackendServiceData = _.cloneDeep(backendServiceToSave);
      this.setPageLoadingStatus(false);
    },

    async loadCertificates() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/certificates`,
        config: { headers: { 'x-fields': 'id, san' } },
      });
      this.certificates = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];
      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['backend_service']);
        });
      });
      this.referencedIds = _.uniq(referencedIds);
      this.setPageLoadingStatus(false);
    },

    async loadNamespace() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `db/system`,
      });
      const featureToggle = response?.data?.['feature-toggle'];
      if (featureToggle) {
        this.isServerToBackendMtlsCertificateEnabled =
          !!featureToggle['ssl-server-to-backend-mtls-certificate'];
        this.isServerToBackendCaCertificateEnabled =
          !!featureToggle['ssl-server-to-backend-ca-certificate'];
      }
    },

    isHostDownable(host: BackendServiceHost) {
      const backHosts = this.selectedBackendServiceData['back_hosts'];
      const downedHosts = backHosts.filter((backHost) => {
        return backHost.down && backHost.id !== host.id;
      });
      const hostId = host?.id ? host.id : '';
      if (!hostId) {
        return downedHosts.length < backHosts.length;
      }
      return !this.isSingleHost && downedHosts.length < backHosts.length - 1;
    },

    addHost(newHost: BackendServiceHost) {
      newHost.id = DatasetsUtils.generateUUID2();
      this.selectedBackendServiceData['back_hosts'].push(newHost);
    },

    editHost(newHost: BackendServiceHost) {
      const oldHostIndex = _.findIndex(this.selectedBackendServiceData['back_hosts'], (host) => {
        return host.id === newHost.id;
      });
      this.selectedBackendServiceData['back_hosts'][oldHostIndex] = newHost;
      _.forEach(Object.keys(this.$refs.hostsTable.rowsInEditMode), (hostId) => {
        if (!this.isHostDownable(this.$refs.hostsTable.rowsInEditMode[hostId])) {
          this.$refs.hostsTable.rowsInEditMode[hostId].down = false;
        }
      });
    },

    deleteHost(host: BackendServiceHost) {
      const newConfiguration = this.selectedBackendServiceData['back_hosts'].filter((entry) => {
        return host.id !== entry.id;
      });
      this.selectedBackendServiceData['back_hosts'] = _.cloneDeep(newConfiguration);
    },

    addHostPort(host: BackendServiceHost, protocol: 'http_ports' | 'https_ports', newPort: string) {
      const newPortNumber = Number(newPort);
      if (
        !host[protocol].includes(newPortNumber) &&
        !_.isNaN(newPortNumber) &&
        isInteger(newPortNumber) &&
        newPortNumber > 0
      ) {
        host[protocol].push(newPortNumber);
      }
    },

    newBackendService(): BackendService {
      const factory = DatasetsUtils.newEntityFactory['backend-services'];
      return factory && factory();
    },

    onUpdateStickyValue(newStickyValue: string | string[]) {
      if (newStickyValue !== 'customcookie') {
        this.selectedBackendServiceData['sticky_cookie_name'] = '';
      }
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedBackendServiceData, this.entityNotSaved] as const,
        ([formData, entityNotSaved]) => {
          Utils.saveSessionData(formData as GenericObject, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Backend Service', 'Backend Services Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedBackendServiceData = this.newBackendService();
            this.selectedBackendServiceData.name = Utils.generateUniqueEntityName(
              this.selectedBackendServiceData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalBackendServiceData = _.cloneDeep(this.selectedBackendServiceData);
          } else {
            const backendServiceId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === backendServiceId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadBackendService(backendServiceId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const certificatesPromise = this.loadCertificates();
          const namespacePromise = this.loadNamespace();
          await Promise.all([certificatesPromise, namespacePromise]);
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedBackendServiceData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
